import { VariableSelector } from '@atoms/dataSelectors/variableSelector/variableSelector';
import { InputField } from '@atoms/input';
import BlockBase from '@components/dnd/base/blockBase';
import { Column } from '@components/dnd/base/blockBase/body.block.style';
import { useAtom } from '@hooks/useAtom';
import EndControlsFB from '@library/formBuilder/endControls';
import {
  Checkbox,
  FormControl,
  FormLabel,
  ListItemDecorator,
  Option,
  Radio,
  RadioGroup,
  Select,
  type SelectOption
} from '@mui/joy';
import {
  type DNDBlock,
  TO_TEXT_VIEW_TYPES,
  TO_TextIconSchema
} from '@stratumn/studio-shared';
import { AlignLeft, type LucideIcon } from 'lucide-react';
import { observer } from 'mobx-react';
import type { ChangeEvent, ReactNode } from 'react';
import { useParams } from 'react-router-dom';
import { ParamsList } from '@/routes/routes.types';
import { newError } from '@/services/errors/errors';
import { isValidType } from '@/utils/parseZodSchema';

import { LabeledVariable } from '../blocks.style';
import { initialTOTextData } from './text.data';
import { textIconOptions } from './text.utils';

export const TextIcon: LucideIcon = AlignLeft;

const renderSelectedIconOption = (
  option: SelectOption<string> | null
): ReactNode => {
  if (!option) {
    return null;
  }

  if (!isValidType(TO_TextIconSchema, option.value, 'TOTEXT-qlojn1')) {
    throw newError('TOTEXT-qlojn1', `Invalid icon option "${option.value}"`);
  }

  const icon = textIconOptions.find(
    iconOption => iconOption.value === option.value
  )?.icon;

  return (
    <>
      <ListItemDecorator>{icon}</ListItemDecorator>
      {option.label}
    </>
  );
};

const Text = (block: DNDBlock) => {
  const workflowId = useParams()[ParamsList.WorkflowId] as string;

  const atom = useAtom({
    type: 'TO_Text',
    sourceId: block.id,
    parentId: workflowId,
    parentKind: 'traceOverview',
    initialData: {
      ...initialTOTextData,
      /* eslint-disable */
      selectedVariable: block.other.selectedVariableRef!
    }
  });

  if (!atom) {
    return null;
  }

  const onTitleChange = (event: ChangeEvent<HTMLInputElement>) => {
    atom.data.title = event.target.value;
  };

  return (
    <BlockBase
      dataItem={atom}
      dndBlock={block}
      endControls={
        <EndControlsFB dataItem={atom} dndBlock={block} hasRequired={false} />
      }
      icon={TextIcon}
      title={atom.data.title}
    >
      <Column>
        <InputField
          label="Widget title"
          onChange={onTitleChange}
          placeholder="Aa"
          value={atom.data.title}
        />
      </Column>
      <Column $width="200px">
        <FormControl size="sm">
          <FormLabel>State variable</FormLabel>
          <VariableSelector
            disabled
            editable={false}
            selectedRef={atom.data.selectedVariable}
          />
        </FormControl>
      </Column>
      <Column>
        <FormControl size="sm">
          <FormLabel>Display type</FormLabel>
          <RadioGroup name="radio-buttons-group" value={atom.data.viewType}>
            {TO_TEXT_VIEW_TYPES.map(viewType => (
              <Radio
                color="neutral"
                key={viewType}
                label={viewType}
                onChange={e =>
                  (atom.data.viewType = e.target.value as typeof viewType)
                }
                size="sm"
                value={viewType}
              />
            ))}
          </RadioGroup>
        </FormControl>
      </Column>
      <Column>
        <LabeledVariable>
          <FormLabel>Icon</FormLabel>
          <Checkbox
            checked={atom.data.hasIcon}
            color="neutral"
            label="Add an icon"
            onChange={e => (atom.data.hasIcon = e.target.checked)}
            size="sm"
            variant="outlined"
          />
          <Select
            disabled={!atom.data.hasIcon}
            onChange={(_, newValue) => {
              if (newValue) {
                atom.data.icon = newValue;
              }
            }}
            placeholder="Select a Icon…"
            renderValue={renderSelectedIconOption}
            size="sm"
            sx={{
              '--ListItemDecorator-size': '20px',
              marginTop: '10px',
              width: '100%'
            }}
            value={atom.data.icon}
          >
            {textIconOptions.map(iconOption => (
              <Option key={iconOption.value} value={iconOption.value}>
                <ListItemDecorator>{iconOption.icon}</ListItemDecorator>
                {iconOption.label}
              </Option>
            ))}
          </Select>
        </LabeledVariable>
      </Column>
    </BlockBase>
  );
};

export default observer(Text);
